import { requestDrawWithAsk } from "@/lib/tikzAsk";
import type { ToolboxInsertPayload } from "./types";

export interface InsertCall {
	type: string;
	content?: Record<string, unknown>;
}

/**
 * Maps a ToolboxInsertPayload (registry-shaped) into the existing
 * BlockEditor `insertBlock(blockType, content)` signature. Returns
 * null for payloads that don't translate to a block insert (inline
 * marks, unsupported placeholders) — those are routed to the editor
 * directly via `lilia:toolbox-mark` in T04 follow-up.
 */
export function mapInsertPayload(
	payload: ToolboxInsertPayload,
): InsertCall | null {
	const { kind, variant } = payload;
	switch (kind) {
		case "paragraph":
			return { type: "paragraph", content: { text: "" } };
		case "heading":
			return {
				type: "heading",
				content: { text: "", level: parseInt(variant ?? "1", 10) },
			};
		case "blockquote":
			return { type: "blockquote", content: { text: "" } };
		case "callout":
			return {
				type: "callout",
				content: {
					calloutType: "note",
					title: "",
					text: "",
					collapsible: false,
				},
			};
		case "list":
			return {
				type: "list",
				content: { items: [], ordered: variant === "numbered" },
			};
		case "equation":
			return { type: "equation", content: { latex: "", displayMode: true } };
		case "theorem":
			return {
				type: "theorem",
				content: {
					theoremType: variant ?? "theorem",
					title: "",
					text: "",
					label: "",
				},
			};
		case "figure":
			// Draw with Ask Lilia… inserts nothing yet: the dock draws first (isDrawWithAsk).
			if (variant === "tikz-ask") return null;
			// A TikZ figure starts with no source; its split view opens on the gallery.
			if (variant === "tikz") {
				return { type: "figure", content: { kind: "tikz", source: "", caption: "", alt: "" } };
			}
			return { type: "figure", content: { src: "", caption: "", alt: "" } };
		case "code":
			return { type: "code", content: { code: "", language: "javascript" } };
		case "footnote":
			return { type: "footnote", content: { text: "" } };
		case "bibliography":
			return {
				type: "bibliography",
				content: { title: "References", style: "apa", entries: [] },
			};
		case "table":
			return {
				type: "table",
				content: {
					headers: ["Column 1", "Column 2", "Column 3"],
					rows: [
						["", "", ""],
						["", "", ""],
					],
				},
			};
		case "columnBreak":
			return { type: "columnBreak", content: {} };
		case "pageBreak":
			return { type: "pageBreak", content: {} };
		case "mark":
			// Inline marks need direct editor access — returned null so the
			// caller can route via `lilia:toolbox-mark` instead.
			return null;
		default:
			return null;
	}
}

/**
 * Insert → Figure → Draw with Ask Lilia… (TikZ 1c): no block yet. The dock
 * opens scoped to a new figure; the figure is inserted when its drawing drew
 * and the author presses Insert figure.
 */
export function isDrawWithAsk(payload: ToolboxInsertPayload): boolean {
	return payload.kind === "figure" && payload.variant === "tikz-ask";
}

// CustomEvent name conventions — kept in one place so listeners and
// dispatchers don't drift.
export const TOOLBOX_INSERT_EVENT = "lilia:toolbox-insert";
export const TOOLBOX_INSERT_BATCH_EVENT = "lilia:toolbox-insert-batch";
export const TOOLBOX_MARK_EVENT = "lilia:toolbox-mark";
export const TOOLBOX_FOCUS_SEARCH_EVENT = "lilia:toolbox-focus-search";
/** Opens the picker over the author's existing tables (EditorTablePicker). */
export const OPEN_TABLE_PICKER_EVENT = "lilia:open-table-picker";

export interface OpenTablePickerDetail {
	/** Sent by the slash menu: the table goes where the `/` was. */
	atSlash?: boolean;
}

/** Ask the editor's host for the existing-table picker. */
export function openTablePicker(detail: OpenTablePickerDetail = {}): void {
	if (typeof window === "undefined") return;
	window.dispatchEvent(new CustomEvent(OPEN_TABLE_PICKER_EVENT, { detail }));
}

export interface ToolboxInsertEventDetail {
	call: InsertCall;
	source: "toolbox" | "slash" | "rail" | "shortcut" | "lorem";
}

/**
 * Batch insert event — dispatched by lorem surfaces (chip / seed /
 * header) so BlockEditor can wrap N paragraphs in a single
 * ProseMirror transaction. Result: one Cmd+Z undoes the entire
 * batch (Engineering brief §C4 acceptance A1.8).
 */
export interface ToolboxInsertBatchEventDetail {
	calls: InsertCall[];
	source: "lorem" | "toolbox";
	/** Sent by the slash menu: the blocks go where the `/` was typed. */
	atSlash?: boolean;
}

export interface ToolboxMarkEventDetail {
	variant: string;
	source: "toolbox" | "slash" | "rail" | "shortcut";
}

/**
 * Shared dispatch for any insert surface (Toolbox, Insert mega menu, slash).
 * Mirrors ToolboxMount.handleInsert so the Insert mega menu (Handoff 26) and
 * the Toolbox emit identical events — one source of insert actions for both.
 * Inline kinds route via the mark event (direct editor access); everything
 * else maps to a block insert.
 */
export function dispatchToolboxInsert(
	payload: ToolboxInsertPayload,
	source: ToolboxInsertEventDetail["source"] = "toolbox",
): void {
	if (typeof window === "undefined") return;
	if (isDrawWithAsk(payload)) {
		requestDrawWithAsk();
		return;
	}
	// Not a new block: a picker over the tables the author already has.
	if (payload.kind === "existingTable") {
		openTablePicker({ atSlash: false });
		return;
	}
	if (
		payload.kind === "mark" ||
		payload.kind === "citation" ||
		payload.kind === "cross-ref" ||
		payload.kind === "footnote" ||
		payload.kind === "embed"
	) {
		const detail: ToolboxMarkEventDetail = {
			variant: payload.variant ?? payload.kind,
			source: source === "lorem" ? "toolbox" : source,
		};
		window.dispatchEvent(new CustomEvent(TOOLBOX_MARK_EVENT, { detail }));
		return;
	}
	const call = mapInsertPayload(payload);
	if (!call) return;
	const detail: ToolboxInsertEventDetail = { call, source };
	window.dispatchEvent(new CustomEvent(TOOLBOX_INSERT_EVENT, { detail }));
}
